import { OrganizationSettingsDTOMaximumPolicyCoverEnum, OrganizationSettingsDTOPricingRuleEnum } from '@reposit/api-client';
import { Formik } from 'formik';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import Modal from '../../components/Modal';
import { Header3, P2 } from '../../components/Typography';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  UPDATE_ORGANIZATION_PRICING_RULE_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_PRICING_RULE_STORE_KEY,
  updateOrganizationPricingRuleRequested,
} from '../../redux/organization/organization.actions';
import { isCappedPricingRule } from './policy-cover-cap-rules';

export type PricingRuleLabels = {
  [key in OrganizationSettingsDTOPricingRuleEnum]: string;
};

interface EditPricingFeeModalProps {
  organizationId: string;
  policyCoverCap?: OrganizationSettingsDTOMaximumPolicyCoverEnum;
  pricingRule?: OrganizationSettingsDTOPricingRuleEnum;
  pricingRuleLabels: PricingRuleLabels;
  onDismiss: () => void;
}

interface EditPricingFeeFormValues {
  pricingRule: OrganizationSettingsDTOPricingRuleEnum;
}

const editablePricingRuleOptions: OrganizationSettingsDTOPricingRuleEnum[] = [
  OrganizationSettingsDTOPricingRuleEnum.STANDARD,
  OrganizationSettingsDTOPricingRuleEnum.STANDARDCAP350,
  OrganizationSettingsDTOPricingRuleEnum.STANDARD15PERCENTDISCOUNT,
];

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const LegacyPricingRuleWarning = styled(P2)`
  background: ${(props) => props.theme.colors.warning};
  border-radius: 8px;
  color: #fff;
  margin: 0 0 16px;
  padding: 12px 16px;
`;

const EditPricingFeeModal: React.FC<EditPricingFeeModalProps> = ({
  organizationId,
  policyCoverCap = OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS,
  pricingRule,
  pricingRuleLabels,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORGANIZATION_PRICING_RULE_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_ORGANIZATION_PRICING_RULE_MODAL_STORE_KEY]);
  const canUseCappedPricingRules = policyCoverCap === OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS;
  const pricingRuleOptions = editablePricingRuleOptions.filter(
    (option) => canUseCappedPricingRules || !isCappedPricingRule(option)
  );
  const hasUnsupportedPricingRule = !!pricingRule && !pricingRuleOptions.includes(pricingRule);
  const unsupportedPricingRuleLabel = pricingRule ? pricingRuleLabels[pricingRule] : undefined;
  const legacyPricingRuleWarningMessage = `This organisation currently has an unsupported pricing fee: ${unsupportedPricingRuleLabel}. Saving will update it to a supported pricing fee.`;
  const initialPricingRule = !hasUnsupportedPricingRule
    ? pricingRule
    : OrganizationSettingsDTOPricingRuleEnum.STANDARD;
  const validationSchema = Yup.object().shape({
    pricingRule: Yup.string().oneOf(pricingRuleOptions, 'Capped fees require a £5,000 policy cover cap').required('Required'),
  });

  return (
    <Modal onDismiss={onDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 15 }}>Edit Pricing Fee</Header3>
      {hasUnsupportedPricingRule ? (
        <LegacyPricingRuleWarning>{legacyPricingRuleWarningMessage}</LegacyPricingRuleWarning>
      ) : null}
      <Formik<EditPricingFeeFormValues>
        initialValues={{
          pricingRule: initialPricingRule || OrganizationSettingsDTOPricingRuleEnum.STANDARD,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          dispatch(updateOrganizationPricingRuleRequested({ organizationId, pricingRule: values.pricingRule }));
        }}
      >
        {({ errors, handleBlur, handleSubmit, setFieldValue, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <FieldWithLabel label="Pricing Fee" error={errors.pricingRule} touched={touched.pricingRule}>
              <Select
                name="pricingRule"
                onChange={(value) => setFieldValue('pricingRule', value)}
                onBlur={handleBlur}
                value={values.pricingRule}
              >
                {pricingRuleOptions.map((option) => (
                  <option key={option} value={option}>
                    {pricingRuleLabels[option]}
                  </option>
                ))}
              </Select>
            </FieldWithLabel>
            <Container fluid>
              <Actions>
                <Button buttonType="secondary" noArrow={true} onClick={onDismiss} type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </Actions>
            </Container>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditPricingFeeModal;
